import { useCanEdit } from '@/auth/account';
import { useEffect, useState } from 'react';
import { Check, Trash2 } from 'lucide-react';
import type { Group } from '@/api/types';
import { useCreateGroup, useDeleteGroup, useUpdateGroup } from '@/api/hooks';
import { PALETTE, nextGroupColor } from '@/lib/palette';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Field, TextArea, TextInput } from './ui/Field';
import { ConfirmDialog } from './ui/ConfirmDialog';

interface GroupDialogProps {
  open: boolean;
  /** null → create mode. */
  group: Group | null;
  existingColors: string[];
  onClose: () => void;
}

export function GroupDialog({ open, group, existingColors, onClose }: GroupDialogProps) {
  const isEdit = group !== null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(PALETTE[0]!.light);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canEdit = useCanEdit();

  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const deleteGroup = useDeleteGroup();

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? '');
    setDescription(group?.description ?? '');
    setColor(group?.color ?? nextGroupColor(existingColors));
    setError(null);
  }, [open, group, existingColors]);

  const save = () => {
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Bitte einen Namen angeben.');
      return;
    }
    const payload = {
      name: trimmed,
      description: description.trim() === '' ? null : description.trim(),
      color,
    };
    const onError = (mutationError: Error) => setError(mutationError.message);
    if (isEdit) {
      updateGroup.mutate({ id: group.id, ...payload }, { onSuccess: onClose, onError });
    } else {
      createGroup.mutate(payload, { onSuccess: onClose, onError });
    }
  };

  const pending = createGroup.isPending || updateGroup.isPending;

  return (
    <>
      <Modal
        open={open}
        title={!canEdit ? 'Gruppe ansehen' : isEdit ? 'Gruppe bearbeiten' : 'Neue Gruppe'}
        description="Gruppen bündeln zusammengehörige Investitionen, z. B. „Terrasse“."
        onClose={onClose}
        footer={
          !canEdit ? (
            <Button onClick={onClose}>Schließen</Button>
          ) : (
          <>
            {isEdit ? (
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                className="mr-auto hover:text-[var(--critical)]"
              >
                <Trash2 size={15} />
                Löschen
              </Button>
            ) : null}
            <Button onClick={onClose}>Abbrechen</Button>
            <Button variant="primary" onClick={save} disabled={pending}>
              {pending ? 'Speichern …' : 'Speichern'}
            </Button>
          </>
          )
        }
      >
        {/* Viewers see the same form, but every field is disabled. */}
        <fieldset disabled={!canEdit} className="m-0 min-w-0 border-0 p-0">
        <div className="flex flex-col gap-4">
          <Field label="Name" error={error}>
            {(id) => (
              <TextInput
                id={id}
                value={name}
                autoFocus
                placeholder="z. B. Terrasse"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <Field label="Beschreibung">
            {(id) => (
              <TextArea
                id={id}
                value={description}
                placeholder="Worum geht es in diesem Projekt?"
                onChange={(event) => setDescription(event.target.value)}
              />
            )}
          </Field>

          <fieldset>
            <legend className="mb-2 text-xs font-medium text-[var(--ink-2)]">Farbe</legend>
            <div className="flex flex-wrap gap-2">
              {PALETTE.map((slot) => (
                <button
                  key={slot.light}
                  type="button"
                  aria-label={slot.name}
                  aria-pressed={color === slot.light}
                  title={slot.name}
                  onClick={() => setColor(slot.light)}
                  className="grid h-8 w-8 place-items-center rounded-lg ring-offset-2 ring-offset-[var(--surface)] transition-transform hover:scale-105 aria-pressed:ring-2 aria-pressed:ring-[var(--accent)]"
                  style={{ background: slot.light }}
                >
                  {color === slot.light ? <Check size={15} color="#fff" /> : null}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
        </fieldset>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Gruppe löschen?"
        message="Die Gruppe wird entfernt. Ihre Investitionen bleiben erhalten und stehen danach ohne Gruppe."
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          if (!group) return;
          deleteGroup.mutate(group.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              onClose();
            },
          });
        }}
      />
    </>
  );
}
